import { Baby, Dumbbell, Flower2, Landmark, MapPin, PartyPopper, Sparkles, Star, UtensilsCrossed, Waves, Wifi, Car, Quote } from "lucide-react";
import { mapLink } from "@/lib/format";
import { Reveal } from "@/components/Reveal";

const ICONS = [
  [/pool|swim/i, Waves], [/spa|wellness/i, Flower2], [/gym|fitness/i, Dumbbell], [/kid|child/i, Baby],
  [/dining|restaurant|food|cuisine/i, UtensilsCrossed], [/banquet|event|wedding/i, PartyPopper],
  [/wi-?fi/i, Wifi], [/parking|ev /i, Car], [/heritage|temple/i, Landmark],
];
const iconFor = (a) => (ICONS.find(([re]) => re.test(a)) || [null, Sparkles])[1];

export const AmenitiesSection = ({ amenities = [] }) => {
  if (!amenities.length) return null;
  return (
    <section className="py-20 sm:py-28 bg-[#FDFBF7]" data-testid="home-amenities-section">
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="eyebrow text-[#C28B5E]">Resort amenities</p>
          <h2 className="mt-3 text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">Everything you need, nothing you don't</h2>
        </div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#E2DCD0] border border-[#E2DCD0]">
          {amenities.map((a, i) => {
            const Icon = iconFor(a);
            return (
              <Reveal key={a} delay={(i % 6) * 0.06} className="bg-white h-full">
                <div className="group p-6 sm:p-8 h-full flex flex-col items-start gap-5 transition-colors duration-500 hover:bg-[#1E3B2E]" data-testid={`amenity-${i}`}>
                  <Icon size={30} strokeWidth={1.3} className="text-[#C28B5E] transition-transform duration-500 group-hover:-translate-y-1" />
                  <p className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E3B2E] leading-tight transition-colors duration-500 group-hover:text-[#FDFBF7]">{a}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Stars = ({ n }) => (
  <div className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={15} className={s <= n ? "fill-[#C28B5E] text-[#C28B5E]" : "text-[#E2DCD0]"} />)}
  </div>
);

export const ReviewsSection = ({ reviews = [], mapQuery }) => (
  <section className="py-20 sm:py-28 bg-[#F4EFE6]" data-testid="home-reviews-section">
    <div className="container-x">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <p className="eyebrow text-[#C28B5E]">Guest stories</p>
          <h2 className="mt-3 text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">Memories our guests take home</h2>
        </div>
        <a href={mapLink(mapQuery || "The Ananta Udaipur")} target="_blank" rel="noopener noreferrer" className="btn-ghost text-[#1E3B2E] hover:bg-[#1E3B2E] hover:text-white self-start" data-testid="reviews-google-btn"><MapPin size={14} />Reviews on Google</a>
      </div>
      {reviews.length ? (
        <div className={`mt-12 grid gap-5 ${reviews.length >= 3 ? "md:grid-cols-2 lg:grid-cols-3" : reviews.length === 2 ? "md:grid-cols-2 max-w-4xl mx-auto" : "max-w-2xl mx-auto"}`}>
          {reviews.map((r, i) => (
            <Reveal key={`${r.name}-${i}`} delay={(i % 3) * 0.08} className="h-full">
              <figure className="relative h-full bg-white border border-[#E2DCD0] p-7 sm:p-8 flex flex-col" data-testid={`review-card-${i}`}>
                <Quote size={34} className="text-[#E5D9C5] absolute top-6 right-6" />
                <Stars n={r.rating || 5} />
                <blockquote className="mt-5 font-['Cormorant_Garamond'] text-xl sm:text-2xl leading-snug text-[#1A1A1A] flex-1">“{r.text}”</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-[#E2DCD0]">
                  <p className="text-sm font-medium text-[#1E3B2E]">{r.name}</p>
                  {r.place && <p className="text-xs text-[#6b6b6b] mt-0.5">{r.place}</p>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      ) : (
        <div className="mt-12 bg-white border border-[#E2DCD0] p-10 sm:p-14 text-center" data-testid="reviews-empty">
          <Quote size={40} className="mx-auto text-[#E5D9C5]" />
          <p className="mt-4 font-['Cormorant_Garamond'] text-2xl sm:text-3xl text-[#1E3B2E]">Stayed with us? We'd love to hear your story.</p>
          <p className="mt-3 text-sm text-[#6b6b6b]">Read what guests say about The Ananta Udaipur on Google, or share your own experience.</p>
        </div>
      )}
    </div>
  </section>
);
